import { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Download, Lock, Radar, Sparkles } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { useStripeCheckout } from "@/hooks/useStripeCheckout";
import { downloadBlueprintPdf } from "@/lib/blueprintPdf";
import { getStripeEnvironment } from "@/lib/stripe";


type ProductTrend = {
  id: string;
  title: string;
  summary: string | null;
  niche: string | null;
  tags: string[] | null;
  total_score: number | null;
  velocity_score: number | null;
  volume_score: number | null;
  relevance_score: number | null;
  published_at: string | null;
  is_free_sample: boolean;
  price_cents: number | null;
  purchased: boolean;
  unlocked: boolean;
  blueprint: Record<string, unknown> | null;
};

const scoreLabel = (n: number | null) =>
  typeof n === "number" ? Math.round(n).toString() : "—";

const priceLabel = (cents: number | null) =>
  !cents || cents <= 0 ? "$0" : `$${(cents / 100).toFixed(cents % 100 === 0 ? 0 : 2)}`;

const INCLUDED = [
  "Buyer profile — who is searching and what they already tried",
  "Price band and margin guidance",
  "Three product angles you can launch this week",
  "A 7-day launch plan with daily actions",
  "Channel and keyword starting points",
];

const BlueprintProduct = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { openCheckout, checkoutElement } = useStripeCheckout();
  const [trend, setTrend] = useState<ProductTrend | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [params, setParams] = useSearchParams();
  const [fulfilling, setFulfilling] = useState(false);

  const load = useCallback(async () => {
    const { data, error } = await (supabase as any).rpc("marketplace_trends");
    if (error) {
      setLoading(false);
      return null;
    }
    const match = ((data as ProductTrend[]) ?? []).find((t) => t.id === id) ?? null;
    setTrend(match);
    setNotFound(!match);
    setLoading(false);
    return match;
  }, [id]);

  useEffect(() => {
    void load();
  }, [load, user?.id]);

  // Coming back from a paid checkout: fulfil the purchase server-side straight
  // away (no waiting for the Stripe webhook), then hand over the PDF.
  useEffect(() => {
    if (!params.get("purchased") || !user) return;
    const sessionId = params.get("session_id");
    let cancelled = false;
    setFulfilling(true);
    (async () => {
      if (sessionId && sessionId.startsWith("cs_")) {
        await supabase.functions.invoke("fulfill-checkout", {
          body: { sessionId, environment: getStripeEnvironment() },
        });
      }
      for (let attempt = 0; attempt < 12 && !cancelled; attempt++) {
        const match = await load();
        if (match?.unlocked && match.blueprint) {
          setFulfilling(false);
          downloadBlueprintPdf({
            title: match.title,
            summary: match.summary,
            niche: match.niche,
            tags: match.tags,
            score: match.total_score,
            blueprint: match.blueprint as Record<string, unknown>,
          });
          setParams(new URLSearchParams(), { replace: true });
          return;
        }
        await new Promise((r) => setTimeout(r, 2500));
      }
      if (!cancelled) setFulfilling(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, user?.id, load]);


  const requireAccount = () => {
    navigate(`/auth?mode=signup&redirect=${encodeURIComponent(`/blueprint/${id}`)}`);
  };

  const buyBlueprint = () => {
    if (!user) return requireAccount();
    openCheckout({
      trendId: id,
      customerEmail: user.email ?? undefined,
      userId: user.id,
      returnUrl: `${window.location.origin}/blueprint/${id}?purchased=1&session_id={CHECKOUT_SESSION_ID}`,
    });
  };

  const buyLicence = () => {
    if (!user) return requireAccount();
    openCheckout({
      priceId: "trends_discovery_lifetime_39",
      customerEmail: user.email ?? undefined,
      userId: user.id,
      returnUrl: `${window.location.origin}/checkout/success?session_id={CHECKOUT_SESSION_ID}`,
    });
  };

  const entries = trend?.blueprint
    ? Object.entries(trend.blueprint).filter(([, v]) => v !== null && v !== undefined && v !== "")
    : [];

  return (
    <>
      <Helmet>
        <title>
          {trend ? `${trend.title.slice(0, 45)} — Launch Blueprint` : "Launch Blueprint"}
        </title>
        <meta
          name="description"
          content={
            trend?.summary?.slice(0, 155) ??
            "An AI-scored trend with a complete launch blueprint: buyer profile, price band, product angles and a 7-day launch plan."
          }
        />
        <link rel="canonical" href={`https://trendsdiscovery.com/blueprint/${id}`} />
        <meta property="og:type" content="product" />
        <meta property="og:title" content={trend ? `${trend.title} — Launch Blueprint` : "Launch Blueprint"} />
        <meta property="og:description" content={trend?.summary ?? ""} />
        {trend && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Product",
              name: `${trend.title} — Launch Blueprint`,
              description: trend.summary ?? undefined,
              category: trend.niche ?? undefined,
              brand: { "@type": "Brand", name: "Trends Discovery" },
              offers: {
                "@type": "Offer",
                price: ((trend.price_cents ?? 0) / 100).toFixed(2),
                priceCurrency: "USD",
                availability: "https://schema.org/InStock",
                url: `https://trendsdiscovery.com/blueprint/${trend.id}`,
              },
            })}
          </script>
        )}
      </Helmet>

      <main className="min-h-screen bg-background text-foreground">
        <div className="container max-w-4xl py-16">
          <Link
            to="/marketplace"
            className="inline-flex items-center gap-2 font-mono text-xs text-primary hover:underline"
          >
            <ArrowLeft className="w-3 h-3" /> BACK TO MARKETPLACE
          </Link>

          <div className="mt-8 flex items-center gap-2">
            <span className="grid place-items-center w-7 h-7 rounded bg-primary text-primary-foreground">
              <Radar className="w-4 h-4" />
            </span>
            <span className="font-display font-semibold">Trends.Discovery</span>
          </div>

          {loading && <p className="mt-12 text-sm text-muted-foreground">Loading this blueprint…</p>}

          {!loading && notFound && (
            <div className="mt-12">
              <h1 className="font-display text-3xl font-bold">This blueprint isn't available</h1>
              <p className="mt-3 text-muted-foreground">
                It may have been unpublished.{" "}
                <Link to="/marketplace" className="text-primary hover:underline">
                  Browse the marketplace
                </Link>{" "}
                for everything that's live.
              </p>
            </div>
          )}

          {trend && (
            <>
              <div className="mt-8 flex flex-wrap items-center gap-2">
                {trend.is_free_sample && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-primary/15 text-primary text-[10px] font-mono px-2 py-0.5">
                    <Sparkles className="w-3 h-3" /> FREE SAMPLE
                  </span>
                )}
                {trend.niche && (
                  <span className="font-mono text-[10px] text-muted-foreground uppercase tracking-wider">
                    {trend.niche}
                  </span>
                )}
                {trend.published_at && (
                  <time
                    dateTime={trend.published_at}
                    className="font-mono text-[10px] text-muted-foreground"
                  >
                    {new Date(trend.published_at).toLocaleDateString()}
                  </time>
                )}
              </div>

              <h1 className="mt-4 font-display text-4xl md:text-5xl font-bold">{trend.title}</h1>
              {trend.summary && (
                <p className="mt-4 max-w-2xl text-muted-foreground leading-relaxed">
                  {trend.summary}
                </p>
              )}

              <div className="mt-8 grid gap-4 sm:grid-cols-4">
                {[
                  ["SCORE", trend.total_score],
                  ["VELOCITY", trend.velocity_score],
                  ["VOLUME", trend.volume_score],
                  ["RELEVANCE", trend.relevance_score],
                ].map(([label, value]) => (
                  <div key={label as string} className="rounded-xl border border-border bg-card p-4">
                    <div className="font-display text-2xl font-bold">
                      {scoreLabel(value as number | null)}
                    </div>
                    <div className="font-mono text-[10px] text-muted-foreground">
                      {label as string}
                    </div>
                  </div>
                ))}
              </div>

              {trend.tags?.length ? (
                <ul className="mt-5 flex flex-wrap gap-2">
                  {trend.tags.map((tag) => (
                    <li
                      key={tag}
                      className="rounded-full border border-border px-2 py-0.5 text-xs text-muted-foreground"
                    >
                      {tag}
                    </li>
                  ))}
                </ul>
              ) : null}

              <div className="mt-10 rounded-2xl border border-border bg-card p-6">
                <h2 className="font-display text-2xl font-bold">What you get</h2>
                <ul className="mt-4 space-y-2">
                  {INCLUDED.map((line) => (
                    <li key={line} className="flex gap-2 text-sm text-muted-foreground">
                      <Check className="w-4 h-4 mt-0.5 text-primary shrink-0" /> {line}
                    </li>
                  ))}
                </ul>

                <div className="mt-6 flex flex-wrap items-center gap-3">
                  {trend.unlocked && trend.blueprint ? (
                    <Button
                      variant="hero"
                      onClick={() =>
                        downloadBlueprintPdf({
                          title: trend.title,
                          summary: trend.summary,
                          niche: trend.niche,
                          tags: trend.tags,
                          score: trend.total_score,
                          blueprint: trend.blueprint as Record<string, unknown>,
                        })
                      }
                    >
                      <Download className="w-4 h-4 mr-1" /> Download the blueprint PDF
                    </Button>
                  ) : (
                    <>
                      <Button variant="hero" onClick={buyBlueprint} disabled={fulfilling}>
                        {fulfilling
                          ? "Preparing your PDF…"
                          : `Buy this blueprint — ${priceLabel(trend.price_cents)}`}
                      </Button>
                      <Button variant="outline" onClick={buyLicence}>
                        Get every blueprint — $39 <ArrowRight className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                </div>
                <p className="mt-3 text-xs text-muted-foreground">
                  Instant PDF download after payment. Secure card payment, 14-day refund.
                </p>
              </div>

              <section className="mt-10">
                <h2 className="font-display text-2xl font-bold">
                  {trend.unlocked ? "The full blueprint" : "Inside the blueprint"}
                </h2>
                {trend.unlocked && entries.length ? (
                  <dl className="mt-5 space-y-5">
                    {entries.map(([key, value]) => (
                      <div key={key}>
                        <dt className="font-mono text-[11px] tracking-wider text-primary uppercase">
                          {key.replace(/_/g, " ")}
                        </dt>
                        <dd className="mt-1 text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap">
                          {Array.isArray(value)
                            ? value
                                .map((v) => (typeof v === "object" ? JSON.stringify(v) : String(v)))
                                .join(" · ")
                            : typeof value === "object"
                              ? JSON.stringify(value, null, 2)
                              : String(value)}
                        </dd>
                      </div>
                    ))}
                  </dl>
                ) : (
                  <div className="relative mt-5 rounded-2xl border border-border bg-card p-6">
                    <div className="pointer-events-none select-none blur-sm space-y-3" aria-hidden>
                      {INCLUDED.map((line) => (
                        <p key={line} className="text-sm text-muted-foreground">
                          {line} — {trend.summary ?? trend.title}
                        </p>
                      ))}
                    </div>
                    <p className="mt-4 flex items-center gap-2 font-mono text-xs text-muted-foreground">
                      <Lock className="w-3 h-3" /> UNLOCKS THE MOMENT YOUR PAYMENT CLEARS
                    </p>
                  </div>
                )}
              </section>
            </>
          )}
        </div>
        {checkoutElement}
      </main>
    </>
  );
};

export default BlueprintProduct;
